import api from './api';
import { LLMMetadata } from '../types';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface RoadmapItemRef {
  id: number;
  topic: string;
  character_limit?: number | null;
  word_limit?: number | null;
  limit_type?: 'characters' | 'words' | null;
}

export type EditType = 'elaboration_content' | 'concept_map_field' | 'roadmap_instruction' | 'checklist_item_add' | 'checklist_evaluation';

export interface EditSuggestion {
  edit_type: EditType;
  roadmap_id?: number | null;
  topic?: string | null;
  field?: string | null;
  field_label?: string | null;
  checklist_type?: string | null;
  group?: string | null;
  item_id?: number | null;
  item_text?: string | null;
  evaluation_level?: string | null;
  proposed_content: string;
}

export interface ChatRequest {
  proposal_id?: number | null;
  message: string;
  history: ChatMessage[];
  llm_provider?: string | null;
  llm_model?: string | null;
  proposal_context?: string | null;
  roadmap_items?: RoadmapItemRef[] | null;
  concept_map?: Record<string, unknown> | null;
  checklist_items?: Record<string, unknown>[] | null;
  doc_checklist_items?: Record<string, unknown>[] | null;
  /** Ativa o agente de onboarding (construir proposta pelo chat). */
  onboarding?: boolean;
}

export interface OnboardingStateEvent {
  state: string;
  step: number;
  total_steps: number;
  derived?: Record<string, unknown>;
  autonomo?: boolean;
}

export interface OnboardingToolCall {
  call_id: string;
  name: string;
  args: Record<string, unknown>;
  requires_confirmation: boolean;
}

export interface OnboardingToolResult {
  call_id: string;
  name: string;
  ok: boolean;
  summary: string;
  navigate_tab?: string | null;
}

export interface ChatResponseMetadata extends LLMMetadata {
  file_search_active?: boolean;
}

export interface ChatResponse {
  response: string;
  metadata?: ChatResponseMetadata;
  edit_suggestion?: EditSuggestion | null;
}

export interface StreamCallbacks {
  onChunk: (text: string) => void;
  onMetadata?: (metadata: ChatResponseMetadata) => void;
  onEditSuggestion?: (suggestion: EditSuggestion) => void;
  onError?: (error: string) => void;
  /**
   * finalText: versão final do texto já sem o markup interno de edição
   * (%%EDIT_START%%...%%EDIT_END%%). Deve substituir o texto acumulado
   * durante o streaming, que pode conter esse markup bruto.
   */
  onDone?: (finalText?: string, extra?: { pending_call?: { call_id: string; name: string } | null; created_proposal_id?: number | null; state?: string; step?: number }) => void;
  // Eventos do agente de onboarding (ignorados quando não é fluxo de onboarding).
  onState?: (ev: OnboardingStateEvent) => void;
  onToolCall?: (ev: OnboardingToolCall) => void;
  onToolResult?: (ev: OnboardingToolResult) => void;
  onProposalCreated?: (proposalId: number) => void;
}

export const chatService = {
  sendMessage: async (data: ChatRequest): Promise<ChatResponse> => {
    try {
      const response = await api.post<ChatResponse>('/chat/message', {
        message: data.message,
        history: data.history.filter(m => m.role !== 'system'),
        proposal_id: data.proposal_id ?? null,
        proposal_context: data.proposal_context ?? null,
        llm_provider: data.llm_provider ?? null,
        llm_model: data.llm_model ?? null,
        roadmap_items: data.roadmap_items ?? null,
        concept_map: data.concept_map ?? null,
        checklist_items: data.checklist_items ?? null,
        doc_checklist_items: data.doc_checklist_items ?? null,
      });
      return response.data;
    } catch (error: any) {
      console.error('[chatService] Erro:', error.message);
      throw new Error(
        error.response?.data?.detail || error.message || 'Erro ao enviar mensagem. Tente novamente.',
      );
    }
  },

  sendMessageStream: async (data: ChatRequest, callbacks: StreamCallbacks, signal?: AbortSignal): Promise<void> => {
    const baseURL = api.defaults.baseURL || '';
    const token = localStorage.getItem('prainte_token');

    const response = await fetch(`${baseURL}/chat/stream`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: data.message,
        history: data.history.filter(m => m.role !== 'system'),
        proposal_id: data.proposal_id ?? null,
        proposal_context: data.proposal_context ?? null,
        llm_provider: data.llm_provider ?? null,
        llm_model: data.llm_model ?? null,
        roadmap_items: data.roadmap_items ?? null,
        concept_map: data.concept_map ?? null,
        checklist_items: data.checklist_items ?? null,
        doc_checklist_items: data.doc_checklist_items ?? null,
        onboarding: data.onboarding ?? false,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Stream não disponível');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const payload = JSON.parse(line.slice(6));
          if (payload.type === 'metadata' && callbacks.onMetadata) {
            callbacks.onMetadata({
              provider_used: payload.provider_used,
              model_used: payload.model_used,
              fallback_reason: payload.fallback_reason,
              file_search_active: payload.file_search_active,
            });
          } else if (payload.type === 'chunk') {
            callbacks.onChunk(payload.content);
          } else if (payload.type === 'state') {
            callbacks.onState?.(payload as OnboardingStateEvent);
          } else if (payload.type === 'tool_call') {
            callbacks.onToolCall?.(payload as OnboardingToolCall);
          } else if (payload.type === 'tool_result') {
            callbacks.onToolResult?.(payload as OnboardingToolResult);
          } else if (payload.type === 'created_proposal_id') {
            callbacks.onProposalCreated?.(payload.proposal_id);
          } else if (payload.type === 'done') {
            if (payload.edit_suggestion && callbacks.onEditSuggestion) {
              callbacks.onEditSuggestion(payload.edit_suggestion);
            }
            callbacks.onDone?.(payload.response, {
              pending_call: payload.pending_call ?? null,
              created_proposal_id: payload.created_proposal_id ?? null,
              state: payload.state,
              step: payload.step,
            });
          } else if (payload.type === 'error') {
            callbacks.onError?.(payload.content);
          }
        } catch { /* JSON parse error — ignore */ }
      }
    }
  },

  sendFeedback: async (params: {
    proposalId?: number | null;
    message: string;
    response: string;
    rating: 1 | -1;
    comment?: string;
  }): Promise<void> => {
    try {
      await api.post('/chat/feedback', {
        proposal_id: params.proposalId ?? null,
        message: params.message,
        response: params.response,
        rating: params.rating,
        comment: params.comment ?? null,
      });
    } catch { /* feedback é best-effort */ }
  },

  getHistory: async (proposalId: number): Promise<ChatMessage[]> => {
    try {
      const response = await api.get<{ messages: ChatMessage[] }>(`/chat/history/${proposalId}`);
      return response.data.messages || [];
    } catch {
      return [];
    }
  },

  saveHistory: async (proposalId: number, messages: ChatMessage[]): Promise<void> => {
    try {
      await api.put(`/chat/history/${proposalId}`, { messages });
    } catch { /* best-effort */ }
  },

  deleteHistory: async (proposalId: number): Promise<void> => {
    try {
      await api.delete(`/chat/history/${proposalId}`);
    } catch { /* best-effort */ }
  },
};
